/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

// Financial calculation helper functions

// Helper function to calculate total order sales from line items
export const calculateGrossTotal = (lineItems: any[]) => {
  if (!lineItems || !Array.isArray(lineItems)) {
    return 0;
  }
  return lineItems.reduce((total, item) => {
    return total + (parseFloat(item.totalPrice) || 0);
  }, 0);
};

// Helper function to calculate gross total including shipping
export const calculateGrossTotalWithShipping = (lineItems: any[], shopifyShipping: number = 0) => {
  const grossTotal = calculateGrossTotal(lineItems);
  const shipping = parseFloat(shopifyShipping?.toString()) || 0;
  return grossTotal + shipping;
};

export const calculateNetPayment = (grossTotal: number, shopifyPaymentFee: number) => {
  const fee = parseFloat(shopifyPaymentFee?.toString()) || 0;
  return grossTotal - fee;
};

export const calculateNetTotal = (netPayment: number, fulfillmentCost: number) => {
  return netPayment - fulfillmentCost;
};

export const calculateMerchSherpaFee = (netTotal: number) => {
  return netTotal * 0.20;
};

export const calculateNetProfit = (netTotal: number, merchSherpaFee: number) => {
  return netTotal - merchSherpaFee;
};

// export const calculateTotalEarned = (netProfit: number, tip: number = 0) => {
//   const tipAmount = parseFloat(tip?.toString()) || 0;
//   return netProfit + tipAmount;
// };
export const calculateTipAfterFees = (
  totalEarned: number,
  netProfit: number
) => {
  return totalEarned - netProfit;
};
export const calculateTotalEarned = (
  netProfit: number, 
  tip: number, 
  grossTotalWithShippingAndTip: number, 
  shopifyPaymentFee: number
) => {
  if (tip <= 0 || grossTotalWithShippingAndTip <= 0) {
    return netProfit;
  }
  
  // Calculate what portion of the payment platform fee was charged on the tip
  //const paymentFeeOnTip = (tip / grossTotalWithShippingAndTip) * shopifyPaymentFee;
  
  // Total earned is net profit + the payment fee that was taken from the tip
  return netProfit; // + paymentFeeOnTip;
};

// Whether Printful actually fulfilled this order at all. Printful cost
// data (and any discount) can still be attached to an order even when
// Printful never touched it — e.g. an item went out of stock and Printful
// canceled the order before production, and it was manually re-fulfilled
// entirely through Yoycol instead. That leftover cost/discount data
// reflects what Printful WOULD have charged, not what actually happened,
// so every calculation below must treat it as if it doesn't exist.
const printfulNeverFulfilled = (order: any) =>
  order.printfulShipping?.status === 'canceled' && !order.printfulShipping?.shipped;

// Helper function to get order-level Printful costs
export const getOrderPrintfulCosts = (order: any) => {
  const printfulSkipped = printfulNeverFulfilled(order);

  const orderCosts = (order.printfulOrderCosts && !printfulSkipped) ? order.printfulOrderCosts : {
    subtotal: 0,
    retailDeliveryFee: 0,
    shipping: 0,
    tax: 0,
    total: 0,
    digitization: 0
  };
  
  return {
    subtotal: parseFloat(orderCosts.subtotal) || 0,
    retailDeliveryFee: parseFloat(orderCosts.retailDeliveryFee) || 0,
    shipping: parseFloat(orderCosts.shipping) || 0,
    tax: parseFloat(orderCosts.tax) || 0,
    total: parseFloat(orderCosts.total) || 0,
    digitization: parseFloat(orderCosts.digitization) || 0,
    additionalFees: 0,
    totalPrintfulCosts: printfulSkipped ? 0 : (parseFloat(order.printfulTotalFees) || 0)
  };
};

// Mirrors the same "did Printful actually fulfill this order" check as
// getOrderPrintfulCosts, so a leftover discount can't survive as a bonus
// on an order Printful never actually touched. Both call sites that used
// to read order.printfulDiscount directly (ShopifySalesGrid and
// OrderTotalsExpanded) should use this instead.
export const getOrderPrintfulDiscount = (order: any) => {
  if (printfulNeverFulfilled(order)) return 0;
  return parseFloat(order.printfulDiscount?.toString()) || 0;
};

// Helper function to get order-level Yoycol costs — mirrors getOrderPrintfulCosts.
// Sourced from order.yoycolOrderCosts, set server-side in
// get-collection-sales-data-from-firestore.mjs from the uploaded Yoycol CSV
// (yoycol_orders in Firestore), aggregated once per distinct Yoycol order
// (not per line item, so multi-item Yoycol orders aren't double-counted).
// Yoycol has no digitization or retail-delivery-fee concept, so those are
// simply absent here — combine with Printful's via getOrderFulfillmentCost
// rather than assuming every vendor has every field.
export const getOrderYoycolCosts = (order: any) => {
  const orderCosts = order.yoycolOrderCosts || {
    subtotal: 0,
    shipping: 0,
    tax: 0,
    total: 0
  };

  return {
    subtotal: parseFloat(orderCosts.subtotal) || 0,
    shipping: parseFloat(orderCosts.shipping) || 0,
    tax: parseFloat(orderCosts.tax) || 0,
    total: parseFloat(orderCosts.total) || 0,
  };
};

// When ANY fulfillment vendor charged real shipping on this order (Printful
// or Yoycol), the Shopify shipping charge the customer paid is real revenue
// that should count toward Cash In — it should be ADDED, not netted against
// or dropped. The vendor's actual shipping cost (from getOrderPrintfulCosts /
// getOrderYoycolCosts) is what should still show and get subtracted as the
// Fulfillment Shipping cost — never Shopify's charge itself.
export const getShopifyShippingRevenue = (order: any) => {
  const printful = getOrderPrintfulCosts(order);
  const yoycol = getOrderYoycolCosts(order);
  if (printful.shipping > 0 || yoycol.shipping > 0) {
    return parseFloat(order.shopifyShippingCharged) || 0;
  }
  return 0;
};

// Combined fulfillment cost across every vendor (Printful + Yoycol so far).
// Single source of truth for this formula — both ShopifySalesGrid's
// calculateOrderTotalEarned and OrderTotalsExpanded need the identical
// number, and having it duplicated in two places is exactly how it drifted
// out of sync with Yoycol in the first place.
export const getOrderFulfillmentCost = (order: any) => {
  const printful = getOrderPrintfulCosts(order);
  const yoycol = getOrderYoycolCosts(order);

  return printful.subtotal + printful.digitization + printful.shipping +
         printful.retailDeliveryFee + printful.tax +
         yoycol.subtotal + yoycol.shipping + yoycol.tax;
};

// Helper function to get adjusted fulfillment costs when Shopify shipping is used
// When customer pays for shipping in Shopify, we exclude Printful shipping from fulfillment costs
export const getAdjustedFulfillmentCost = (orderPrintfulCosts: any, shopifyShipping: number = 0) => {
  const shipping = parseFloat(shopifyShipping?.toString()) || 0;
  
  // If customer paid for shipping in Shopify, exclude Printful shipping from fulfillment cost
  if (shipping > 0) {
    return orderPrintfulCosts.total - orderPrintfulCosts.shipping;
  }
  
  // Otherwise, use the full Printful cost including their shipping
  return orderPrintfulCosts.total;
};

// End-to-end "what does this order actually net the seller" calculation -
// the single source of truth both ShopifySalesGrid's collapsed row and
// AdminPayouts' "amount earned since last payout" figure need to agree on.
export const calculateOrderTotalEarned = (order: any) => {
  // Refunded orders never actually kept any revenue - show $0 rather
  // than a calculated figure that implies money was earned.
  if (order.financialStatus?.toLowerCase() === 'refunded') {
    return 0;
  }

  const grossTotal = calculateGrossTotal(order.lineItems);
  const shopifyPaymentFee = parseFloat(order.shopifyPaymentFee?.toString()) || 0;
  const tip = parseFloat(order.shopifyTip?.toString()) || 0;
  const printfulDiscount = getOrderPrintfulDiscount(order);

  const calculatedFulfillmentCost = getOrderFulfillmentCost(order);
  const shopifyShippingRevenue = getShopifyShippingRevenue(order);

  const grossTotalWithTip = grossTotal + tip + shopifyShippingRevenue;
  const netPayment = calculateNetPayment(grossTotalWithTip, shopifyPaymentFee);
  const netTotal = calculateNetTotal(netPayment, calculatedFulfillmentCost);

  const adjustedNetTotal = netTotal + printfulDiscount;
  const merchSherpaFee = calculateMerchSherpaFee(adjustedNetTotal);
  const netProfit = calculateNetProfit(adjustedNetTotal, merchSherpaFee);

  return netProfit;
};
